import { css } from '@emotion/react';
import React from 'react';
import { AngularContext } from 'react-app';
import t from 'react-translate';
import { getCourseRolePlays } from 'redux/actions/ai-role-play';
import { getFeatureableExercises } from 'redux/actions/exercises';
import { getCourseScenarios } from 'redux/actions/video-practice';
import { useAppDispatch } from 'redux/store';
import { wrapThunkAction } from 'redux/utils';
import { useSelector } from 'react-redux';
import { getCurrentCourse } from 'redux/selectors/course';
import NvDropdown, {
  NvDropdownAccordionHeader,
  NvDropdownAlign,
  NvDropdownButtonStyle,
  NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import NvIcon from 'shared/components/nv-icon';
import { useCourseHomeUtils } from 'shared/hooks/use-course-home-utils';
import LoadingSpinner from 'shared/components/loading-spinner';
import { black, gray2, warning, white } from 'styles/global_defaults/colors';
import { dropdownZIndex, standardSpacing } from 'styles/global_defaults/scaffolding';
import { VideoPracticeScenario } from 'redux/schemas/models/video-practice';
import { CourseExercise } from 'redux/schemas/models/exercise';

type AiRolePlay = {
  id: number;
  title: string;
};

type Props = {
  /**
   * Renders the component as an inline mobile gallery panel.
   * When true: data is fetched on mount instead of lazily on dropdown open,
   * the dropdown is forced open so accordion items are immediately visible,
   * and the desktop icon/trigger is hidden.
   * Used by MobileSubmissionsGalleryMenu in nv-header-page-links when the
   * user taps "Gallery" in the mobile three-dot nav.
   */
  isMobileMenu?: boolean;
};

const styles = css`
  height: inherit;

  button {
    padding: 0;
    border: none;
  }

  .bs4-dropdown-toggle::after {
    height: inherit;
    content: none;
    display: none;
  }

  &.is-white-icon .icon-wrapper,
  .is-white-icon .icon-wrapper {
    color: ${white};
  }

  .nv-dropdown,
  .bs4-dropdown,
  .nv-dropdown [role='button'] {
    height: inherit;
  }

  .bs4-dropdown-menu {
    width: 220px;
    max-width: 220px;

    .bs4-dropdown-item {
      color: ${black};
      white-space: initial;
      line-height: ${standardSpacing}px;
    }

    .accordion-header {
      i.icon {
        color: ${gray2};
      }

      &.expanded i.icon {
        color: ${warning};
      }
    }
  }
  .submissions-gallery-menu {
    max-height: calc(100vh - 150px); /* 150px = top bar + bottom bar + buffer */
    overflow-y: auto;
    z-index: ${dropdownZIndex + 1};
  }
`;

const loadingItemStyle = css`min-height: 110px`;

const SubmissionsGalleryMenu = ({ isMobileMenu = false }: Props) => {
  const { injectServices } = React.useContext(AngularContext);
  const [$state, $stateParams] = injectServices(['$state', '$stateParams']);
  const { isWhiteIcon, isEditOrReorderMode } = useCourseHomeUtils();
  const dispatch = useAppDispatch();
  const currentCourse = useSelector(getCurrentCourse);

  const [featureableExercises, setFeatureableExercises] = React.useState<CourseExercise[]>([]);
  const [aiRolePlays, setAiRolePlays] = React.useState<AiRolePlay[]>([]);
  const [scenarios, setScenarios] = React.useState<VideoPracticeScenario[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [expandedAccordionId, setExpandedAccordionId] = React.useState<string | null>(null);
  const hasFetched = React.useRef(false);
  const isFetching = React.useRef(false);
  const isMounted = React.useRef(true);
  const hasInitializedExpanded = React.useRef(false);

  React.useEffect(() => () => { isMounted.current = false; }, []);

  React.useEffect(() => {
    hasFetched.current = false;
    isFetching.current = false;
    hasInitializedExpanded.current = false;
    setExpandedAccordionId(null);
    setFeatureableExercises([]);
    setAiRolePlays([]);
    setScenarios([]);
    setIsLoading(true);
  }, [$stateParams.catalogId]);

  // Expand the first available accordion once after data loads
  React.useEffect(() => {
    if (isLoading || hasInitializedExpanded.current) return;
    if (featureableExercises.length > 0) {
      hasInitializedExpanded.current = true;
      setExpandedAccordionId('assignments-submissions');
    } else if (scenarios.length > 0) {
      hasInitializedExpanded.current = true;
      setExpandedAccordionId('video-practice-scenarios');
    } else if (aiRolePlays.length > 0) {
      hasInitializedExpanded.current = true;
      setExpandedAccordionId('ai-roleplays-submissions');
    }
  }, [isLoading, featureableExercises.length, scenarios.length, aiRolePlays.length]);

  const fetchData = React.useCallback(() => {
    if (!currentCourse) return;
    if (hasFetched.current || isFetching.current) return;
    isFetching.current = true;
    setIsLoading(true);
    let anySucceeded = false;
    const sharedParams = {
      catalogId: $stateParams.catalogId,
      completedSubmissionOnly: true,
    };
    const tasks = [
      wrapThunkAction<CourseExercise[]>(dispatch(getFeatureableExercises(sharedParams)))
        .then((result) => { anySucceeded = true; if (isMounted.current) setFeatureableExercises(result.payload); })
        .catch(() => null),
      wrapThunkAction<AiRolePlay[]>(dispatch(getCourseRolePlays({ ...sharedParams, nameList: true })))
        .then((result) => { anySucceeded = true; if (isMounted.current) setAiRolePlays(result.payload); })
        .catch(() => null),
      wrapThunkAction<VideoPracticeScenario[]>(dispatch(getCourseScenarios({ ...sharedParams, nameList: true })))
        .then((result) => { anySucceeded = true; if (isMounted.current) setScenarios(result.payload); })
        .catch(() => null),
    ];
    Promise.all(tasks).then(() => {
      // Leave hasFetched false on total failure so the next open retries
      if (anySucceeded) hasFetched.current = true;
    }).finally(() => {
      isFetching.current = false;
      if (isMounted.current) setIsLoading(false);
    });
  }, [$stateParams.catalogId, dispatch, currentCourse]);

  // When used as the mobile gallery panel, fetch immediately on mount
  React.useEffect(() => {
    if (isMobileMenu) {
      fetchData();
    }
  }, [isMobileMenu, fetchData]);

  const isSelected = $state.current?.name === 'submissions-gallery' || $state.includes('submissions-gallery');

  const handleToggle = React.useCallback((isOpen: boolean) => {
    if (!isOpen) return;
    fetchData();
  }, [fetchData]);

  const renderTarget = React.useCallback(() => (
    <> {!isMobileMenu && (
      <>
        <div className='icon-wrapper hidden-xs' role='presentation'>
          <NvIcon icon='gallery' size='small' aria-hidden='true' />
        </div>
        <span className='visible-xs'>{t.COURSE_HOME.HEADER.GALLERY()}</span>
        <div className='selected-indicator' />
      </>
    )}
    </>
  ), [isMobileMenu]);

  const dataItems = React.useMemo(() => [
    ...(featureableExercises.length > 0 ? [{
      type: 'accordion-header' as const,
      id: 'assignments-submissions',
      expanded: expandedAccordionId === 'assignments-submissions',
      onToggle: (isExpanded: boolean) => setExpandedAccordionId(isExpanded ? 'assignments-submissions' : null),
      title: t.COURSE_HOME.HEADER.GALLERY_MENU.ASSIGNMENTS(),
      items: [
        {
          type: 'text' as const,
          id: 'all',
          text: t.EXERCISES.ALL_ASSIGNMENTS(),
          callback: () => $state.go('submissions-gallery', { catalogId: $stateParams.catalogId }),
        },
        ...featureableExercises.map((exercise) => ({
          type: 'text' as const,
          id: exercise.id,
          text: exercise.title,
          callback: () => $state.go('submissions-gallery', { catalogId: $stateParams.catalogId, exerciseId: exercise.id }),
        })),
      ],
    }] : []),
    ...(scenarios.length > 0 ? [{
      type: 'accordion-header' as const,
      id: 'video-practice-scenarios',
      expanded: expandedAccordionId === 'video-practice-scenarios',
      onToggle: (isExpanded: boolean) => setExpandedAccordionId(isExpanded ? 'video-practice-scenarios' : null),
      title: t.COURSE_HOME.HEADER.GALLERY_MENU.VIDEO_PRACTICE(),
      items: scenarios.map((scenario) => ({
        type: 'text' as const,
        id: scenario.id,
        text: scenario.title,
        callback: () => $state.go('practice-room-modal', { scenarioId: scenario.id }),
      })),
    }] : []),
    ...(aiRolePlays.length > 0 ? [{
      type: 'accordion-header' as const,
      id: 'ai-roleplays-submissions',
      expanded: expandedAccordionId === 'ai-roleplays-submissions',
      onToggle: (isExpanded: boolean) => setExpandedAccordionId(isExpanded ? 'ai-roleplays-submissions' : null),
      title: t.COURSE_HOME.HEADER.GALLERY_MENU.AI_ROLEPLAYS(),
      items: aiRolePlays.map((rolePlay) => ({
        type: 'text' as const,
        id: rolePlay.id,
        text: rolePlay.title,
        callback: () => $state.go('role-play-submission-modal', { catalogId: $stateParams.catalogId, rolePlayId: rolePlay.id }),
      })),
    }] : []),
  ] as NvDropdownAccordionHeader[], [$state, $stateParams, featureableExercises, aiRolePlays, scenarios, expandedAccordionId]);

  const loadingItem: NvDropdownOption[] = [{
    type: 'custom' as const,
    customItem: <div css={loadingItemStyle} className='d-flex align-items-center'><LoadingSpinner /></div>,
  }];
  const items: NvDropdownOption[] = isLoading ? loadingItem : dataItems;

  return (
    <div
      css={styles}
      className={`page-link ${isSelected ? 'selected ' : ''}${isWhiteIcon ? 'is-white-icon' : ''}`}
    >
      <NvDropdown
        buttonStyle={NvDropdownButtonStyle.CUSTOM}
        items={items}
        align={NvDropdownAlign.RIGHT}
        noMenuPadding
        showSelectedIndicator
        isFocusable={!isEditOrReorderMode}
        onToggle={handleToggle}
        altLabel={t.COURSE_HOME.HEADER.GALLERY()}
        menuClassName='submissions-gallery-menu'
        show={isMobileMenu || undefined}
        customTarget={renderTarget}
      />
    </div>
  );
};

export default SubmissionsGalleryMenu;
